import React, { useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { usePortalNavigate } from '@/hooks/usePortalPath';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons';
import { Button } from '@/components';
import { Colors } from '@core/config';
import { useUserStore } from '@core/user/useUserStore';
import { useThemeStore } from '@core/theme/useThemeStore';
import DateRangePicker from '@/components/DateRangePicker';

interface HeaderProps {
  startDate: Dayjs | undefined;
  endDate: Dayjs | undefined;
  handleDateChange: (startDate: Dayjs, endDate: Dayjs) => void;
  refreshData: () => void;
}

const Header = ({
  startDate,
  endDate,
  handleDateChange,
  refreshData,
}: HeaderProps) => {
  const { currentUser } = useUserStore();

  const navigate = usePortalNavigate();

  const theme = useThemeStore();
  const dateRangeValue = useMemo(
    () => ({
      start: startDate?.toDate() ?? new Date(),
      end: endDate?.toDate() ?? new Date(),
    }),
    [startDate, endDate],
  );

  return (
    <div className="@container mb-4">
      <div className="flex flex-col gap-3 @4xl:flex-row @4xl:items-end @4xl:justify-between">
        <div className="min-w-0">
          <div className="mb-2 text-2xl font-semibold text-gray-800">
            {currentUser?.firstName
              ? `Hi, ${currentUser?.firstName}!`
              : 'Dashboard'}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
            <span className="whitespace-nowrap">
              {dayjs().format('dddd, MMM D, YYYY')}
            </span>
            <button
              type="button"
              className="inline-flex items-center whitespace-nowrap"
              onClick={refreshData}
            >
              <FontAwesomeIcon
                style={{
                  color: theme?.theme?.primaryBrandColor || Colors.LINK_BLUE,
                }}
                icon={faArrowsRotate}
                className="mr-1"
              />
              <span>Last updated {dayjs().format('h:mm A')}</span>
            </button>
          </div>
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <DateRangePicker
            value={dateRangeValue}
            onChange={(start, end) =>
              handleDateChange(dayjs(start), dayjs(end))
            }
            placeholder="Select date"
            primaryBrandColor={
              theme?.theme?.primaryBrandColor || Colors.LINK_BLUE
            }
            panelMargin="mt-4"
          />
          <Button
            variant="primary"
            className="bg-accent-900 hover:bg-accent-700 shrink-0 cursor-pointer whitespace-nowrap px-3 py-1 text-sm"
            onClick={() =>
              navigate('/jobs/new', { state: { from: 'dashboard' } })
            }
          >
            + Post a job
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Header;
